import React from 'react';
import { Users, AlertCircle, CheckCircle } from 'lucide-react';

const SeatAllocationCard = ({ seatInfo, teamTier }) => {
  const usagePercentage = seatInfo ? (seatInfo?.current_seats / seatInfo?.max_seats) * 100 : 0;
  const isNearLimit = usagePercentage >= 80;
  const isAtLimit = usagePercentage >= 100;

  const getTierColor = (tier) => {
    switch (tier) {
      case 'enterprise':
        return 'text-purple-600';
      case 'draftiq_plus':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Seat Allocation</h3>
        <Users className="w-5 h-5 text-gray-400" />
      </div>

      {seatInfo ? (
        <>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-2xl font-bold text-gray-900">
                {seatInfo?.current_seats} / {seatInfo?.max_seats}
              </span>
              <span className={`text-sm font-medium ${getTierColor(teamTier)}`}>
                {teamTier === 'draftiq_plus' ? 'DraftIQ Plus' : teamTier?.charAt(0)?.toUpperCase() + teamTier?.slice(1)}
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  isAtLimit
                    ? 'bg-red-500'
                    : isNearLimit
                    ? 'bg-yellow-500' :'bg-emerald-500'
                }`}
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Seats Used</span>
              <span className="font-medium text-gray-900">{seatInfo?.current_seats}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">Available Seats</span>
              <span className="font-medium text-gray-900">{seatInfo?.available_seats}</span>
            </div>
          </div>

          {/* Status Alert */}
          {isAtLimit ? (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900">Seat Limit Reached</p>
                <p className="text-xs text-red-700 mt-1">
                  Upgrade your plan to add more team members
                </p>
              </div>
            </div>
          ) : isNearLimit ? (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Approaching Seat Limit</p>
                <p className="text-xs text-yellow-700 mt-1">
                  Only {seatInfo?.available_seats} seats remaining
                </p>
              </div>
            </div>
          ) : (
            <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-emerald-900">Seats Available</p>
                <p className="text-xs text-emerald-700 mt-1">
                  You can invite {seatInfo?.available_seats} more members
                </p>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">Loading seat information...</p>
        </div>
      )}
    </div>
  );
};

export default SeatAllocationCard;